import React, { Component } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import { compose } from 'redux';
import {
  reduxForm,
  Field,
  change,
  getFormValues,
  formValueSelector,
  reset,
  destroy,
} from 'redux-form';
import PropTypes from 'prop-types';
import { isEmpty, isNil, debounce, capitalize, startCase } from 'lodash';
import ButtonMenu from 'components/buttons/ButtonMenu';
import DOMPurify from 'dompurify';
import MenuItem from 'components/Menu/MenuItem';
import Button from 'components/buttons/Button';
import { Box, IconButton } from '@material-ui/core';
import ExpandMoreIcon from 'mdi-react/ExpandMoreIcon';
import DotsHorizontalIcon from 'mdi-react/DotsHorizontalIcon';
import { ReactComponent as QualityCheckIcon } from 'icons/qualityCheck.svg';
import RenderSwitchInput from 'components/forms/RenderSwitchInput';
import Menu from 'components/Menu/Menu';
import ConfirmationModal from 'components/modals/ConfirmationModal';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import { getLetterInAlphabet, truncateText, normalizeString } from 'utils/misc';
import { isSafari } from 'utils';
import classNames from 'classnames';
import {
  requiredValidator,
  requiredQuillValidator,
  handleBlur,
} from 'utils/validators';
import { unescapeHtml, stripHTMLTags, filterLegacyTemplateFields } from 'utils/steps';
import RenderFormInput from 'components/forms/RenderFormInput';
import TestEmailModal from 'components/modals/TestEmailModal/index';
import { Can, AbilityContext } from 'utils/permissions';
import { SUBJECT_INHERITED } from 'constants/pitch';
import Alert from 'components/Alert';
import { Editor } from '@tinymce/tinymce-react';
import {
  getPitches,
  getTemplatePitches,
  getPitchQualityMetrics,
  getPitchSize,
  getPitchCompletion,
  initializePitchQualityMetrics,
} from 'redux/actions/pitchActions';
import { apiPitchUploadImage, apiValidatePitch } from 'constants/apiRouting';


class Pitch extends Component {
  static propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    pristine: PropTypes.bool,
    invalid: PropTypes.bool,
    submitting: PropTypes.bool,
    id: PropTypes.number,
    name: PropTypes.string,
    subject: PropTypes.string,
    body: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.string),
    is_template: PropTypes.bool.isRequired,
    currentStepIndex: PropTypes.number.isRequired,
    currentPitchIndex: PropTypes.number.isRequired,
    onExpandPitch: PropTypes.func.isRequired,
    collapsed: PropTypes.bool.isRequired,
    onCancelPitch: PropTypes.func.isRequired,
    getPitches: PropTypes.func.isRequired,
    onDestroyPitch: PropTypes.func.isRequired,
    setFormNotSaved: PropTypes.func.isRequired,
    csvTemplateFields: PropTypes.shape({
      loading: PropTypes.bool,
      loaded: PropTypes.bool,
      results: PropTypes.array,
    }).isRequired,
  };
  static defaultProps = {
    name: null,
    subject: null,
    body: null,
    id: null,
    pristine: true,
    invalid: true,
    submitting: false,
    errors: [],
  };
  constructor(props) {
    super(props);
    this.state = {
      testEmailModalOpen: false,
      subjectDisabled:
        props.currentStepIndex > 0
          ? props.initialValues.inherits_subject
          : false,
      isTemplate: this.props.is_template,
      templateMenuOpened: false,
      uploading: false,
      validating: false,
      errors: props.errors || [],
      templateParams: {
        account: this.props.sequence.account_id,
        page: this.props.pitches.nextTemplateUrl
          ? new URL(this.props.pitches.nextTemplateUrl).searchParams.get('page')
          : 1,
      },
      areYouSureModalIsOpen: false,
      emailSizeCheck: {
        images: 0,
      },
    };
    this.quillRef = null;
    this.safariEditor = null;
    this.editorRef = React.createRef();
    this.selector = formValueSelector(props.form);
    this.updatePitchPreviewDataDebounced = debounce(
      props.updatePitchPreviewData,
      500,
    );
  }

  componentDidMount = () => {
    this.setupCustomEditorSolution();
  };

  componentDidUpdate = (prevProps) => {
    const body = this.selector(this.props.state, 'body');
    const prevBody = this.selector(prevProps.state, 'body');
    const subject = this.selector(this.props.state, 'subject');
    const prevSubject = this.selector(prevProps.state, 'subject');
    const normalizedBody = normalizeString(body);
    const normalizedPrevBody = normalizeString(prevBody);

    if (normalizedBody !== normalizedPrevBody || normalizeString(subject) !== normalizeString(prevSubject)) {
      this.updatePitchPreviewDataDebounced({ subject, body });
      this.setState({ validating: true });
      this.validatePitch({ subject, body });

      if (this.props.pitchQualityCheckOpen) {
        this.checkPitchQuality({ subject, body });
      }

      if (this.props.pristine !== prevProps.pristine && !this.props.pristine) {
        this.props.setFormNotSaved(true);
      }
    }
  };

  componentWillUnmount = () => {
    this.clearEditorListeners();
  };

  // Safari Only
  onEditorInput = () => {
    if (window.getSelection) {
      this.savedRange = window.getSelection().getRangeAt(0);
    }
  };

  imageUploadHandler = async (blobInfo) => {
    const formData = new FormData();
    formData.append('file', blobInfo.blob(), blobInfo.filename());
    const { data } = await axios.post(apiPitchUploadImage(), formData, {
      withCredentials: true,
    });
    return data.file_url;
  };

  // Safari Only
  onEditorFocus = () => {
    if (this.savedRange != null && window.getSelection) {
      const s = window.getSelection();
      if (s.rangeCount > 0) {
        s.removeAllRanges();
      }
      s.addRange(this.savedRange);
    }
  };

  onDestroyPitch = (id, currentStepIndex, currentPitchIndex) => {
    this.props.onDestroyPitch(id, currentStepIndex, currentPitchIndex);
    this.props.dispatch(destroy(`pitch-${currentStepIndex}-${currentPitchIndex}`));
    this.handleMoreClose();
  };

  onChangeIsTemplate = (event) => {
    this.setState({ isTemplate: event.target.checked });
  };

  onPitchCollapseToggle = () => {
    const {
      collapsed,
      onExpandPitch,
      currentStepIndex,
      currentPitchIndex,
    } = this.props;
    if (collapsed) return onExpandPitch(currentStepIndex, currentPitchIndex);
    return this.cancelPitch();
  };

  // Safari Only
  setupCustomEditorSolution = () => {
    if (isSafari()) {
      this.safariEditor = document.querySelector('.ql-editor');
      this.safariEditor.addEventListener('mouseup', this.onEditorInput);
      this.safariEditor.addEventListener('keyup', this.onEditorInput);
      this.safariEditor.addEventListener('focus', this.onEditorFocus);
    }
  };

  setUploading = (value) => {
    this.setState({ uploading: value });
  };

  validatePitch = debounce(async (values) => {
    const { data } = await axios.post(apiValidatePitch(), values, {
      withCredentials: true,
    });
    this.setState({ validating: false, errors: data.errors || [] });
  }, 500);

  checkPitchQuality = debounce(async (values) => {
    const bodyText = this.editorRef.current.getContent();
    if (!bodyText || bodyText === '\n') {
      this.props.initializePitchQualityMetrics();
      return;
    }
    this.props.getPitchQualityMetrics({
      subject: values.subject,
      body: bodyText,
    });
    const images = this.editorRef.current.contentDocument.querySelectorAll('img');
    if (
      this.state.emailSizeCheck.images.length !== Array.from(images).length ||
      isNil(this.props.emailSize)
    ) {
      this.props.getPitchSize(values);
      this.setState({
        emailSizeCheck: {
          ...this.state.emailSizeCheck,
          images,
        },
      });
    }
  }, 500);

  // Safari Only
  clearEditorListeners = () => {
    if (isSafari()) {
      this.safariEditor.removeEventListener('mouseup', this.onEditorInput);
      this.safariEditor.removeEventListener('keyup', this.onEditorInput);
      this.safariEditor.removeEventListener('focus', this.onEditorFocus);
    }
  };

  templateSelected(pitchTemplate) {
    const { form } = this.props;
    this.toggleTemplateMenu();
    this.props.dispatch(change(form, 'name', pitchTemplate.name));
    this.props.dispatch(change(form, 'subject', pitchTemplate.subject));
    this.props.dispatch(change(form, 'body', pitchTemplate.body));
    this.props.dispatch(change(form, 'is_template', false));
  }

  handleOnChangeInherit = (event) => {
    this.setState({ subjectDisabled: event.target.checked });
  };

  toggleEmailModal = () => {
    const { testEmailModalOpen } = this.state;
    this.setState(
      {
        testEmailModalOpen: !testEmailModalOpen,
      },
      this.handleMoreClose(),
    );
  };

  editPitch = () => {
    const { onExpandPitch, currentStepIndex, currentPitchIndex } = this.props;
    onExpandPitch(currentStepIndex, currentPitchIndex);
    this.handleMoreClose();
  };

  handleMoreClick = (event) => {
    this.setState({ anchorEl: event.currentTarget });
  };

  handleMoreClose = () => {
    this.setState({ anchorEl: null });
  };

  handleOnChangeImg = input => (allDeltas, editor) => {
    input.onChange(allDeltas);
    input.onChange(editor.getHTML());
    this.setState({ uploading: false });
  };

  handleOnChange = input => (value) => {
    input.onChange(value);
  };

  toggleTemplateMenu = (ref) => {
    this.setState({ templateMenuOpened: Boolean(ref) });
  };

  handleScroll = (e) => {
    const bottom =
      Math.round(e.target.scrollHeight - e.target.scrollTop) ===
      e.target.clientHeight;
    if (bottom && this.props.pitches.nextTemplateUrl) {
      this.props.getTemplatePitches(this.state.templateParams);
    }
  };

  cancelPitch = () => {
    if (!this.props.pristine) {
      this.setState({ areYouSureModalIsOpen: true });
    } else {
      this.props.onCancelPitch(
        this.props.currentStepIndex,
        this.props.currentPitchIndex,
      );
      this.props.togglePitchPreview(false);
      this.props.togglePitchQualityCheck(false);
    }
  };

  toggleAreYouSure(answer) {
    if (answer) {
      this.props.dispatch(reset(this.props.form));
      this.props.togglePitchQualityCheck(false);
      this.setState(
        {
          areYouSureModalIsOpen: false,
        },
        () => {
          this.props.onCancelPitch(
            this.props.currentStepIndex,
            this.props.currentPitchIndex,
          );
          this.props.togglePitchPreview(false);
        },
      );
    } else {
      this.setState({ areYouSureModalIsOpen: false });
    }
  }

  hasChanged = () => {
    const formValues = getFormValues(this.props.form)(this.props.state);
    return formValues && (formValues.subject || formValues.body);
  };

  togglePitchPreview = () => {
    this.props.togglePitchPreview();
    const data = {
      subject: this.selector(this.props.state, 'subject'),
      body: this.selector(this.props.state, 'body'),
    };
    this.updatePitchPreviewDataDebounced(data);
  };

  togglePitchQualityCheck = () => {
    const subject = this.selector(this.props.state, 'subject');
    const body = this.selector(this.props.state, 'body');
    if (!this.props.pitchQualityCheckOpen) { this.checkPitchQuality({ subject, body }); }
    this.props.togglePitchQualityCheck();
  };

  parseErrors = errors =>
    ['subject', 'body']
      .map((field) => {
        const errorKey = `Error parsing ${field}: `;
        const error = errors.find(err => err.includes(errorKey));
        if (error) {
          return `Error in ${capitalize(field)} field: Template cannot be parsed: ${error.split(errorKey)[1]}`;
        }
        return null;
      })
      .filter(Boolean);

  renderQuill = ({ input }) =>
    (this.context.can('edit', 'Step') ? (
      <>
        <Editor
          apiKey="ckhfzoj2pneepbp2uyfc2kn0xxk6u2uzfqvjr07d5evi6iad"
          onInit={(evt, editor) => {
            this.editorRef.current = editor;

            // Change the icon of "merge tags" to be "{}"
            const buttons = document.querySelectorAll('button[title="Insert merge tag"] .tox-icon');

            for (let i = 0; i < buttons.length; i += 1) {
                buttons[i].innerHTML = `
                    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <text x="6" y="15" font-family="Arial" font-size="20" font-weight="bold" fill="black">{}</text>
                    </svg>
                `;
            }
          }}
          // initialValue="<p>This is the initial content of the editor.</p>"
          {...input}
          onEditorChange={this.handleOnChange(input)}
          init={{
            height: 500,
            menubar: true,
            plugins: [
              'ai', 'mentions', 'autolink', 'charmap', 'emoticons', 'image', 'link', 'lists', 'searchreplace', 'wordcount', 'checklist', 'casechange', 'export', 'formatpainter', 'permanentpen', 'mergetags', 'powerpaste', 'tinymcespellchecker', 'autocorrect', 'a11ychecker', 'typography', 'inlinecss',
            ],
            toolbar: 'mergetags | aidialog aishortcuts | bold italic underline strikethrough | fontfamily fontsize | image link emoticons charmap | alignment formatting indentation | removeformat | undo redo',
            toolbar_mode: 'floating',
            toolbar_groups: {
              formatting: {
                icon: 'ordered-list',
                tooltip: 'Lists',
                items: 'checklist numlist bullist',
              },
              indentation: {
                icon: 'indent',
                tooltip: 'Indentation',
                items: 'indent outdent',
              },
              alignment: {
                icon: 'align-left',
                tooltip: 'Alignment',
                items: 'align lineheight',
              },
            },
            content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px } p { margin: 0; }',
            contextmenu: 'advtemplate',
            mergetags_list: filterLegacyTemplateFields(this.props.csvTemplateFields.results).sort((a, b) => a.name.localeCompare(b.name)).map(field => ({
              value: field.name,
              title: startCase(field.name),
            })),
            mergetags_prefix: '{{ ',
            mergetags_suffix: ' }}',
            ai_request: (request, respondWith) => {
              const completion = this.props.getPitchCompletion(this.props.id, { prompt: request.prompt });
              respondWith.string(() => completion);
            },
            images_upload_handler: this.imageUploadHandler,
          }}
        />
      </>
    ) : (
      <div
        className="p-3"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(input.value) }}
      />
    ));

  render() {
    const { subjectDisabled } = this.state;
    const {
      name,
      subject,
      body,
      collapsed,
      currentStepIndex,
      currentPitchIndex,
      handleSubmit,
      pitches,
      id,
      pristine,
      submitting,
      invalid,
      templatePitches,
    } = this.props;

    const strippedBody = body ? unescapeHtml(stripHTMLTags(body)) : '';

    const { testEmailModalOpen, anchorEl, templateMenuOpened } = this.state;
    const labelIsTemplateId = `isTemplate-${currentStepIndex}-${currentPitchIndex}`;

    return (
      <div
        className={classNames('pitch', { 'pitch--collapsed': collapsed })}
        key={`pitch-${currentPitchIndex}`}
      >
        <TestEmailModal
          isOpen={testEmailModalOpen}
          pitchId={id}
          toggle={this.toggleEmailModal}
        />
        <div className="pitch__collapsed-title__toolbar">
          <DotsHorizontalIcon
            id={`pitch-menu-${currentStepIndex}-${currentPitchIndex}`}
            aria-controls="more-menu"
            aria-haspopup="true"
            onClick={this.handleMoreClick}
          />
          <ExpandMoreIcon
            onClick={this.onPitchCollapseToggle}
            style={
              collapsed
                ? {
                    transform: 'rotate(180deg)',
                    transition: 'transform 0.3s ease-in-out',
                  }
                : {
                    transform: 'rotate(0deg)',
                    transition: 'transform 0.3s ease-in-out',
                  }
            }
          />
          <Menu
            id="more-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={this.handleMoreClose}
          >
            <Can I="edit" a="Step">
              <MenuItem onClick={this.editPitch}>Edit</MenuItem>
            </Can>
            <Can I="delete" a="Step">
              <MenuItem
                onClick={() =>
                  this.onDestroyPitch(id, currentStepIndex, currentPitchIndex)
                }
              >
                Delete
              </MenuItem>
            </Can>
          </Menu>
        </div>
        {collapsed && (
          <div
            className={classNames('pitch__collapsed-title', {
              error: !isEmpty(this.state.errors),
            })}
          >
            <span className="pitch__collapsed-title__sequence-letter">
              {getLetterInAlphabet(currentPitchIndex).toUpperCase()}
            </span>
            <span className="pitch__collapsed-title__title">
              <strong>
                {subjectDisabled
                  ? SUBJECT_INHERITED
                  : truncateText(subject, 40)}
              </strong>{' '}
              - {truncateText(strippedBody, 60)}
            </span>
            <Box
              component="div"
              className="pitch__collapsed-title__expand-click"
              onClick={this.onPitchCollapseToggle}
            />
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="pitch__content">
            {!subjectDisabled && (
              <div
                className={classNames('pitch__field pitch__subject', {
                  'border-bottom':
                    currentStepIndex > 0 && this.context.can('edit', 'Step'),
                })}
              >
                <span className="pitch__variant">
                  {getLetterInAlphabet(currentPitchIndex).toUpperCase()}
                </span>
                <div className="subject__input">
                  <Field
                    maxLength="255"
                    name="subject"
                    label="Subject"
                    value={subject}
                    noBorder
                    component={RenderFormInput}
                    type="text"
                    onBlur={handleBlur}
                    disabled={
                      subjectDisabled || !this.context.can('edit', 'Step')
                    }
                    validate={[requiredValidator]}
                  />
                </div>
              </div>
            )}
            {currentStepIndex > 0 && this.context.can('edit', 'Step') && (
              <div
                className="pitch__btn_container"
                style={{
                  margin: '0.5rem',
                  top: subjectDisabled ? '0.313rem' : undefined,
                }}
              >
                <div className="switch__container">
                  {/* eslint-disable jsx-a11y/label-has-for */}
                  <label htmlFor={labelIsTemplateId} />
                  <Field
                    id={labelIsTemplateId}
                    name="inherits_subject"
                    component={RenderSwitchInput}
                    onChange={this.handleOnChangeInherit}
                  />
                  <p>Inherit subject from previous step</p>
                </div>
              </div>
            )}
            <div className="pitch__body">
              <Can I="edit" a="Step">
                <div className="body__field-container">
                  <ButtonMenu
                    className="d-inline-block"
                    btnText="Select a template"
                    btnClick={this.toggleTemplateMenu}
                    open={templateMenuOpened}
                    onScroll={this.handleScroll}
                    loadingPitch={pitches.loading}
                  >
                    {templatePitches.results.map(pitch => (
                      <MenuItem
                        key={`template-${pitch.id}`}
                        onClick={() => this.templateSelected(pitch)}
                      >
                        {pitch.name}
                      </MenuItem>
                    ))}
                    {isEmpty(templatePitches.results) && (
                      <MenuItem key="no-pitches">
                        No templates existing
                      </MenuItem>
                    )}
                  </ButtonMenu>
                  {this.props.togglePitchPreview && (
                    <div className="d-inline-block float-right">
                      <IconButton
                        color="primary"
                        aria-label="show preview"
                        onClick={() => this.togglePitchPreview()}
                      >
                        <VisibilityOutlinedIcon />
                      </IconButton>
                    </div>
                  )}
                  {this.props.togglePitchQualityCheck && (
                    <div className="d-inline-block float-right">
                      <IconButton
                        color="primary"
                        aria-label="show quality check"
                        onClick={() => this.togglePitchQualityCheck()}
                      >
                        <QualityCheckIcon />
                      </IconButton>
                    </div>
                  )}
                </div>
              </Can>
              <Field
                name="body"
                component={this.renderQuill}
                validate={[requiredQuillValidator]}
              />
              {this.parseErrors(this.state.errors).map(error => (
                <Alert color="danger" className="mt-2" key={error} noDismiss>
                  <span>{error}</span>
                </Alert>
              ))}
            </div>

            <div className="pitch__btn_container">
              <Can I="edit" a="Step">
                <div className="switch__container">
                  {/* eslint-disable jsx-a11y/label-has-for */}
                  <label htmlFor="saveTemplate" />
                  <Field
                    id="saveTemplate"
                    name="is_template"
                    component={RenderSwitchInput}
                    onChange={this.onChangeIsTemplate}
                    validate={[]}
                  />
                  <p>Save as template</p>
                  {this.state.isTemplate && (
                    <Field
                      maxLength="255"
                      name="name"
                      label="Pitch name"
                      component={RenderFormInput}
                      value={name}
                      type="text"
                      onBlur={handleBlur}
                      validate={[requiredValidator]}
                    />
                  )}
                </div>
              </Can>
              <Button
                color="secondary"
                variant="text"
                onClick={this.cancelPitch}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                variant="contained"
                type="submit"
                className="ml-2"
                loading={this.state.uploading || this.state.validating}
                disabled={pristine || invalid || submitting}
              >
                Save
              </Button>
            </div>
          </div>
          <ConfirmationModal
            open={this.state.areYouSureModalIsOpen}
            onBackdropClick={() => this.toggleAreYouSure(false)}
            title="Confirm"
            confirmLabel="Yes"
            cancelLabel="No"
            onConfirmClick={() => this.toggleAreYouSure(true)}
            onCancelClick={() => this.toggleAreYouSure(false)}
          >
            All changes will be lost. <br />
            Are you sure want to cancel?
          </ConfirmationModal>
        </form>
      </div>
    );
  }
}

Pitch.contextType = AbilityContext;

export default compose(
  connect(
    state => ({
      sequence: state.sequence,
      pitches: state.pitches,
      templatePitches: state.templatePitches,
      csvTemplateFields: state.csvTemplateFields,
      emailSize: state.pitchQualityMetrics.email_size,
      state,
    }),
    {
      getPitches,
      getTemplatePitches,
      getPitchQualityMetrics,
      getPitchSize,
      getPitchCompletion,
      initializePitchQualityMetrics,
    },
  ),
  reduxForm({ enableReinitialize: true }),
)(Pitch);
